// @contexts/AccountClient.context.tsx
import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import * as Option from "@utils/Option";
import type { OptionTs } from "@ps-bridge/interop";
import type { AccountClient } from '@services/clients/AccountClient';
import { fromInteropAccountClient } from '@services/clients/AccountClient';
import { createDefaultAccountClient } from "@src/purescript/interop";
import { StubAccountClient } from '@services/stubs/StubAccountClient';
import { enabledFeatures, FeaturesEnum } from '@src/utils/enabledFeatures';
import { useJwtController } from '@contexts/Jwt.context';
import { requireContext } from '@contexts/requireContext';

const AccountClientContext = createContext<OptionTs<AccountClient>>(Option.none());

export const AccountClientProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { markUnauthorized } = useJwtController();
    // Create the account client, conditionally using the StubAccountClient if the flag is set
    const accountClient = useMemo<AccountClient>(() => {
        if (enabledFeatures[FeaturesEnum.StubClients]) {
            return new StubAccountClient();
        }
        return fromInteropAccountClient(createDefaultAccountClient(markUnauthorized));
    }, [markUnauthorized]);

    const contextValue = useMemo(
        () => Option.some(accountClient),
        [accountClient]
    );

    return (
        <AccountClientContext.Provider value={contextValue}>
            {children}
        </AccountClientContext.Provider>
    );
};

export const useAccountClient = (): AccountClient => {
    return requireContext(
        useContext(AccountClientContext),
        'useAccountClient must be used within an AccountClientProvider',
    );
};
